import type { AdminBillingData, AdminBillingPackage, AdminBillingPlan, AdminPanelData, BillingStatsRange } from '@dreamquiz/shared';

type Provider = AdminPanelData['settings']['aiProvider'];

/**
 * Klickbares Admin-Panel (Wunsch Niggo 2026-09-13). Bewusste Ausnahme von
 * "alles per Texteingabe" (Konzept Kap. 7) - Betriebswerkzeug für den
 * App-Admin, kein Teil des Quiz-Erlebnisses. Der Server prüft die Rolle bei
 * JEDER Aktion neu (siehe net/socketAdapter.ts) - dieses Modul zeigt nur an
 * und reicht Klicks weiter, entscheidet selbst nichts.
 */
export interface AdminPanelActions {
  setProvider(provider: Provider): void;
  setModel(model: string): void;
  setKey(provider: 'openai' | 'anthropic', key: string): void;
  addAdmin(handle: string): void;
  removeAdmin(handle: string): void;
  refresh(): void;
  /** Zahlungen (Wunsch Niggo 2026-09-13). */
  setBillingEnabled(enabled: boolean): void;
  saveBillingPackage(input: Omit<AdminBillingPackage, 'id'> & { id?: string }): void;
  deleteBillingPackage(id: string): void;
  saveBillingPlan(input: Omit<AdminBillingPlan, 'id'> & { id?: string }): void;
  deleteBillingPlan(id: string): void;
  setBillingStatsRange(range: BillingStatsRange): void;
  setHostingCost(cents: number): void;
}

function must<T extends HTMLElement>(id: string): T {
  const el = document.getElementById(id);
  if (!el) throw new Error(`Element #${id} fehlt im HTML`);
  return el as T;
}

/** Wie must(), nur für das <svg>-Umsatzdiagramm - SVGSVGElement ist kein HTMLElement. */
function mustSvg(id: string): SVGSVGElement {
  const el = document.getElementById(id);
  if (!el) throw new Error(`Element #${id} fehlt im HTML`);
  return el as unknown as SVGSVGElement;
}

/** "4.99" -> 499. Rundet, damit Tippfehler wie "4.999" nicht krumme Cent-Beträge erzeugen. */
function euroToCents(value: string): number {
  const normalized = value.trim().replace(',', '.');
  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? Math.round(parsed * 100) : 0;
}

function centsToEuro(cents: number): string {
  return (cents / 100).toFixed(2);
}

export class AdminPanel {
  private readonly overlay = must('admin-overlay');
  private readonly tabs = Array.from(document.querySelectorAll<HTMLButtonElement>('.admin-tab'));
  private readonly views = Array.from(document.querySelectorAll<HTMLElement>('.admin-view'));
  /** Neues, noch ungespeichertes Rundenpaket/Abo - lokal verwaltet, bis actions.save…() erfolgreich war und ein render() von neuen Serverdaten kommt. */
  private draftPackage = false;
  private draftPlan = false;

  constructor(private readonly actions: AdminPanelActions) {
    must('admin-close').addEventListener('click', () => this.close());
    this.overlay.addEventListener('click', (event) => {
      if (event.target === this.overlay) this.close();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isOpen) this.close();
    });

    for (const tab of this.tabs) {
      tab.addEventListener('click', () => this.selectTab(tab.dataset['tab']!));
    }

    must<HTMLSelectElement>('admin-provider').addEventListener('change', (event) => {
      const provider = (event.target as HTMLSelectElement).value as Provider;
      must('admin-key-provider-label').textContent = provider;
      this.actions.setProvider(provider);
    });

    must('admin-save-model').addEventListener('click', () => {
      const input = must<HTMLInputElement>('admin-model');
      if (input.value.trim()) this.actions.setModel(input.value.trim());
    });

    must('admin-save-key').addEventListener('click', () => {
      const keyInput = must<HTMLInputElement>('admin-key-input');
      const provider = must<HTMLSelectElement>('admin-provider').value;
      if (!keyInput.value.trim()) return;
      if (provider !== 'openai' && provider !== 'anthropic') return;
      this.actions.setKey(provider, keyInput.value.trim());
      keyInput.value = ''; // Nie wieder anzeigen - dieselbe Regel wie /admin ai key im Terminal.
    });

    must('admin-add-btn').addEventListener('click', () => this.submitAdd());
    must<HTMLInputElement>('admin-add-input').addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.submitAdd();
      }
    });

    must<HTMLInputElement>('admin-billing-enabled').addEventListener('change', (event) => {
      this.actions.setBillingEnabled((event.target as HTMLInputElement).checked);
    });
    must('admin-billing-add-package').addEventListener('click', () => {
      this.draftPackage = true;
      this.renderPackages(this.lastPackages);
    });
    must('admin-billing-add-plan').addEventListener('click', () => {
      this.draftPlan = true;
      this.renderPlans(this.lastPlans);
    });
    for (const btn of Array.from(document.querySelectorAll<HTMLButtonElement>('#admin-billing-range .admin-range-btn'))) {
      btn.addEventListener('click', () => {
        const range = btn.dataset['range'] as BillingStatsRange;
        this.actions.setBillingStatsRange(range);
      });
    }
    must('admin-billing-save-hosting-cost').addEventListener('click', () => {
      const input = must<HTMLInputElement>('admin-billing-hosting-cost');
      if (!input.value.trim()) return;
      this.actions.setHostingCost(euroToCents(input.value));
    });
  }

  private lastPackages: AdminBillingPackage[] = [];
  private lastPlans: AdminBillingPlan[] = [];

  get isOpen(): boolean {
    return !this.overlay.hidden;
  }

  open(): void {
    this.overlay.hidden = false;
    this.actions.refresh();
  }

  close(): void {
    this.overlay.hidden = true;
  }

  render(data: AdminPanelData): void {
    must<HTMLSelectElement>('admin-provider').value = data.settings.aiProvider;
    must<HTMLInputElement>('admin-model').value = data.settings.aiModel;
    must('admin-key-provider-label').textContent = data.settings.aiProvider;
    must('admin-key-status').textContent =
      data.settings.aiProvider === 'mock'
        ? 'Mock-Provider braucht keinen Key.'
        : `Key gesetzt: ${data.settings.hasKey ? 'ja' : 'nein'}`;

    this.renderStats('admin-cost-grid', [
      ['Aufrufe (24h)', String(data.cost.calls)],
      ['Token in/out (24h)', `${data.cost.totalIn} / ${data.cost.totalOut}`],
      ['Kosten (24h)', `~$${data.cost.totalCost.toFixed(4)}`],
      ['Insgesamt', `${data.cost.allTimeCalls} Aufrufe · ~$${data.cost.allTimeCost.toFixed(4)}`],
    ]);

    this.renderStats('admin-stats-grid', [
      ['Aktive Sitzungen', String(data.stats.activeSessions)],
      ['Aktive Räume', String(data.stats.activeRooms)],
      ['Spieler insgesamt', String(data.stats.totalPlayers)],
      ['Runden insgesamt', String(data.stats.totalRounds)],
    ]);

    const topList = must('admin-top-players');
    topList.replaceChildren();
    for (const p of data.stats.topPlayers) {
      const li = document.createElement('li');
      li.style.listStyle = 'none';
      li.textContent = `${p.name} · ${p.score} Pkt`;
      topList.appendChild(li);
    }
    if (!data.stats.topPlayers.length) {
      const li = document.createElement('li');
      li.style.listStyle = 'none';
      li.style.color = 'var(--mu)';
      li.textContent = 'Noch keine Runden gespielt.';
      topList.appendChild(li);
    }

    const adminList = must('admin-list');
    adminList.replaceChildren();
    for (const handle of data.admins) {
      const li = document.createElement('li');
      const span = document.createElement('span');
      span.textContent = handle;
      li.appendChild(span);
      if (data.admins.length > 1) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.textContent = '×';
        btn.setAttribute('aria-label', `${handle} entfernen`);
        btn.addEventListener('click', () => this.actions.removeAdmin(handle));
        li.appendChild(btn);
      }
      adminList.appendChild(li);
    }

    this.renderPool(data.pool);
    this.renderBilling(data.billing);
  }

  private renderPool(pool: AdminPanelData['pool']): void {
    must('admin-pool-summary').textContent = pool.totalQuestions
      ? `${pool.totalQuestions} wiederverwendbare Fragen insgesamt - werden vor neuen KI-Aufrufen zuerst genutzt.`
      : 'Noch keine Fragen im Pool - füllt sich, sobald die KI aktiv Fragen erzeugt.';

    const container = must('admin-pool-areas');
    container.replaceChildren();
    for (const area of pool.areas) {
      const details = document.createElement('details');
      details.className = 'admin-pool-area';

      const summary = document.createElement('summary');
      summary.textContent = `${area.icon} ${area.areaName} (${area.questions.length})`;
      details.appendChild(summary);

      const list = document.createElement('ol');
      list.className = 'admin-pool-questions';
      for (const q of area.questions) {
        const li = document.createElement('li');

        const prompt = document.createElement('div');
        prompt.className = 'admin-pool-prompt';
        prompt.textContent = q.prompt;
        li.appendChild(prompt);

        const answer = document.createElement('div');
        answer.className = 'admin-pool-answer';
        answer.textContent = `✔ ${q.answer}`;
        li.appendChild(answer);

        if (q.options?.length) {
          const options = document.createElement('div');
          options.className = 'admin-pool-options';
          options.textContent = q.options.join(' · ');
          li.appendChild(options);
        }

        list.appendChild(li);
      }
      details.appendChild(list);
      container.appendChild(details);
    }
  }

  /* ------------------------------------------------------------ Zahlungen */

  private renderBilling(billing: AdminBillingData): void {
    must<HTMLInputElement>('admin-billing-enabled').checked = billing.paymentsEnabled;
    must('admin-billing-stripe-hint').textContent = billing.stripeConfigured
      ? ''
      : '⚠ STRIPE_SECRET_KEY/STRIPE_WEBHOOK_SECRET fehlen in der .env - Zahlungen können trotz Freischaltung nicht funktionieren.';

    this.lastPackages = billing.packages;
    this.lastPlans = billing.plans;
    this.renderPackages(billing.packages);
    this.renderPlans(billing.plans);

    this.renderBillingStats(billing);
  }

  private renderBillingStats(billing: AdminBillingData): void {
    const hostingCostInput = must<HTMLInputElement>('admin-billing-hosting-cost');
    if (document.activeElement !== hostingCostInput) hostingCostInput.value = centsToEuro(billing.hostingCostCents);

    for (const btn of Array.from(document.querySelectorAll<HTMLButtonElement>('#admin-billing-range .admin-range-btn'))) {
      btn.classList.toggle('admin-range-active', btn.dataset['range'] === billing.statsRange);
    }

    const rows: [string, string][] = [
      ['Umsatz', `${centsToEuro(billing.stats.totalCents)} €`],
      ['Pakete verkauft', String(billing.stats.packageCount)],
      ['Abos gestartet', String(billing.stats.subscriptionStartedCount)],
      ['Abos aktiv', String(billing.stats.activeSubscriptions)],
      ['Abos gekündigt (laufen noch)', String(billing.stats.cancelledSubscriptions)],
      ['Abos beendet', String(billing.stats.deactivatedSubscriptions)],
      ['Fest für API-Credits', `${centsToEuro(billing.stats.fixedApiPortionCents)} €`],
      ['Echter KI-Verbrauch', `~$${billing.stats.apiCostUsd.toFixed(4)}`],
      ['davon Admins verbraucht', `~$${billing.stats.adminCostUsd.toFixed(4)}`],
      [
        'Gewinn',
        billing.stats.profitEur === null ? 'unbekannt (Hostingkosten eintragen)' : `~${billing.stats.profitEur.toFixed(2)} €`,
      ],
    ];
    this.renderStats('admin-billing-stat-grid', rows);
    this.renderChart(billing.stats.buckets, billing.statsRange);
  }

  private renderPackages(packages: AdminBillingPackage[]): void {
    const container = must('admin-billing-packages');
    container.replaceChildren();
    if (this.draftPackage) {
      container.appendChild(
        this.buildPackageRow(null, () => {
          this.draftPackage = false;
        }),
      );
    }
    for (const pkg of packages) container.appendChild(this.buildPackageRow(pkg));
  }

  private buildPackageRow(pkg: AdminBillingPackage | null, onSaved?: () => void): HTMLElement {
    const row = document.createElement('div');
    row.className = 'admin-billing-row';

    const name = document.createElement('input');
    name.type = 'text';
    name.placeholder = 'Name, z. B. "+20 KI-Runden"';
    name.value = pkg?.name ?? '';

    const description = document.createElement('input');
    description.type = 'text';
    description.placeholder = 'Beschreibung (optional)';
    description.value = pkg?.description ?? '';

    const credits = document.createElement('input');
    credits.type = 'number';
    credits.min = '1';
    credits.placeholder = 'Runden';
    credits.value = pkg ? String(pkg.credits) : '';
    credits.className = 'admin-billing-num';

    const price = document.createElement('input');
    price.type = 'number';
    price.min = '0.01';
    price.step = '0.01';
    price.placeholder = 'Preis (€)';
    price.value = pkg ? centsToEuro(pkg.priceCents) : '';
    price.className = 'admin-billing-num';

    const apiPortion = document.createElement('input');
    apiPortion.type = 'number';
    apiPortion.min = '0';
    apiPortion.step = '0.01';
    apiPortion.placeholder = 'fest für API-Credits (€)';
    apiPortion.value = pkg ? centsToEuro(pkg.apiPortionCents) : '';
    apiPortion.className = 'admin-billing-num';
    apiPortion.title = 'Fester Anteil des Preises für API-Credits eingeplant - der Rest zählt zum Gewinn.';

    const active = document.createElement('input');
    active.type = 'checkbox';
    active.checked = pkg?.isActive ?? true;
    active.title = 'aktiv (kaufbar)';

    const save = document.createElement('button');
    save.type = 'button';
    save.className = 'admin-btn admin-btn-round';
    save.textContent = '✔';
    save.setAttribute('aria-label', 'Speichern');
    save.addEventListener('click', () => {
      if (!name.value.trim() || !credits.value || !price.value) return;
      this.actions.saveBillingPackage({
        id: pkg?.id,
        name: name.value.trim(),
        description: description.value.trim(),
        credits: Number.parseInt(credits.value, 10),
        priceCents: euroToCents(price.value),
        apiPortionCents: apiPortion.value ? euroToCents(apiPortion.value) : 0,
        isActive: active.checked,
        sortOrder: pkg?.sortOrder ?? 0,
      });
      onSaved?.();
    });

    row.append(name, description, credits, price, apiPortion, active, save);

    if (pkg) {
      const del = document.createElement('button');
      del.type = 'button';
      del.className = 'admin-btn admin-btn-round';
      del.textContent = '×';
      del.setAttribute('aria-label', 'Löschen');
      del.addEventListener('click', () => this.actions.deleteBillingPackage(pkg.id));
      row.appendChild(del);
    }

    return row;
  }

  private renderPlans(plans: AdminBillingPlan[]): void {
    const container = must('admin-billing-plans');
    container.replaceChildren();
    if (this.draftPlan) {
      container.appendChild(
        this.buildPlanRow(null, () => {
          this.draftPlan = false;
        }),
      );
    }
    for (const plan of plans) container.appendChild(this.buildPlanRow(plan));
  }

  private buildPlanRow(plan: AdminBillingPlan | null, onSaved?: () => void): HTMLElement {
    const row = document.createElement('div');
    row.className = 'admin-billing-row';

    const name = document.createElement('input');
    name.type = 'text';
    name.placeholder = 'Name, z. B. "Starter"';
    name.value = plan?.name ?? '';

    const description = document.createElement('input');
    description.type = 'text';
    description.placeholder = 'Beschreibung (optional)';
    description.value = plan?.description ?? '';

    const price = document.createElement('input');
    price.type = 'number';
    price.min = '0.01';
    price.step = '0.01';
    price.placeholder = 'Preis/30 Tage (€)';
    price.value = plan ? centsToEuro(plan.priceCents) : '';
    price.className = 'admin-billing-num';

    const bonus = document.createElement('input');
    bonus.type = 'number';
    bonus.min = '0';
    bonus.step = '0.01';
    bonus.placeholder = 'Budget-Bonus ($/Monat)';
    bonus.value = plan ? String(plan.monthlyBudgetBonusUsd) : '';
    bonus.className = 'admin-billing-num';

    const apiPortion = document.createElement('input');
    apiPortion.type = 'number';
    apiPortion.min = '0';
    apiPortion.step = '0.01';
    apiPortion.placeholder = 'fest für API-Credits (€)';
    apiPortion.value = plan ? centsToEuro(plan.apiPortionCents) : '';
    apiPortion.className = 'admin-billing-num';
    apiPortion.title = 'Fester Anteil des Preises pro 30-Tage-Periode für API-Credits eingeplant - der Rest zählt zum Gewinn.';

    const active = document.createElement('input');
    active.type = 'checkbox';
    active.checked = plan?.isActive ?? true;
    active.title = 'aktiv (abonnierbar)';

    const save = document.createElement('button');
    save.type = 'button';
    save.className = 'admin-btn admin-btn-round';
    save.textContent = '✔';
    save.setAttribute('aria-label', 'Speichern');
    save.addEventListener('click', () => {
      if (!name.value.trim() || !price.value || !bonus.value) return;
      this.actions.saveBillingPlan({
        id: plan?.id,
        name: name.value.trim(),
        description: description.value.trim(),
        priceCents: euroToCents(price.value),
        monthlyBudgetBonusUsd: Number.parseFloat(bonus.value),
        apiPortionCents: apiPortion.value ? euroToCents(apiPortion.value) : 0,
        isActive: active.checked,
        sortOrder: plan?.sortOrder ?? 0,
      });
      onSaved?.();
    });

    row.append(name, description, price, bonus, apiPortion, active, save);

    if (plan) {
      const del = document.createElement('button');
      del.type = 'button';
      del.className = 'admin-btn admin-btn-round';
      del.textContent = '×';
      del.setAttribute('aria-label', 'Löschen');
      del.addEventListener('click', () => this.actions.deleteBillingPlan(plan.id));
      row.appendChild(del);
    }

    return row;
  }

  /** Handgebautes SVG-Balkendiagramm - keine neue Abhängigkeit, gleicher Stil wie die Sterne in scoreboard.ts. */
  private renderChart(buckets: { label: string; amountCents: number }[], range: BillingStatsRange): void {
    const svg = mustSvg('admin-billing-chart');
    svg.replaceChildren();
    if (!buckets.length) return;

    const ns = 'http://www.w3.org/2000/svg';
    const max = Math.max(...buckets.map((b) => b.amountCents), 1);
    const width = 300;
    const height = 100;
    const gap = 2;
    const barWidth = Math.max(1, width / buckets.length - gap);

    buckets.forEach((bucket, i) => {
      const barHeight = Math.max(1, (bucket.amountCents / max) * (height - 4));
      const rect = document.createElementNS(ns, 'rect');
      rect.setAttribute('x', String(i * (barWidth + gap)));
      rect.setAttribute('y', String(height - barHeight));
      rect.setAttribute('width', String(barWidth));
      rect.setAttribute('height', String(barHeight));
      rect.setAttribute('fill', 'var(--gold, #d4af37)');
      const date = new Date(bucket.label);
      const when = range === '24h' ? date.toLocaleTimeString('de-DE', { hour: '2-digit' }) : date.toLocaleDateString('de-DE');
      const title = document.createElementNS(ns, 'title');
      title.textContent = `${when}: ${centsToEuro(bucket.amountCents)} €`;
      rect.appendChild(title);
      svg.appendChild(rect);
    });
  }

  private submitAdd(): void {
    const input = must<HTMLInputElement>('admin-add-input');
    const handle = input.value.trim();
    if (!handle) return;
    this.actions.addAdmin(handle);
    input.value = '';
  }

  private renderStats(gridId: string, rows: [label: string, value: string][]): void {
    const grid = must(gridId);
    grid.replaceChildren();
    for (const [label, value] of rows) {
      const cell = document.createElement('div');
      cell.className = 'admin-stat';
      const v = document.createElement('b');
      v.textContent = value;
      const l = document.createElement('small');
      l.textContent = label;
      cell.appendChild(v);
      cell.appendChild(l);
      grid.appendChild(cell);
    }
  }

  private selectTab(tabName: string): void {
    for (const tab of this.tabs) {
      const active = tab.dataset['tab'] === tabName;
      tab.classList.toggle('active', active);
      tab.setAttribute('aria-selected', String(active));
    }
    let wide = false;
    for (const view of this.views) {
      const active = view.dataset['view'] === tabName;
      view.hidden = !active;
      if (active) wide = view.classList.contains('admin-view-wide');
    }
    this.overlay.querySelector('.admin-panel')!.classList.toggle('admin-panel-wide', wide);
  }
}
